import { useEffect, useState } from 'react'
import { usePrevious } from '../../../helpers/hooks/usePrevious'
import {
  subscribeDepthForSecurity,
  unsubscribeDepthForSecurity
} from '../../../store/depthOfMarket/actions'
import { getDepthOfMarketOrder } from '../../../store/depthOfMarket/selectors'
import {
  getOrderById,
  getOrderByTransactionId
} from '../../../store/order/selectors'
import { Order } from '../../../store/order/types'
import { QuoteReliability } from '../../../store/securities/types'
import { useAppDispatch, useAppSelector } from '../../../store/types'
import {
  fetchDataForSecurityModal,
  unsubscribeFetchDataForSecurityModal
} from '../../../store/windows/actions'
import {
  getAggressorWindowOrder,
  getSecurityModalInfo
} from '../../../store/windows/selectors'
import { AggressorWindowInfo } from '../../../store/windows/types'

export const useAggressorOrder = (initialOrderId: Order['id']) => {
  const getAggressorOrder = useAppSelector(getAggressorWindowOrder)
  const getOrder = useAppSelector(getOrderById)

  const [aggressorOrderId, setAggressorOrderId] = useState<
    Order['id'] | undefined
  >()

  const initialAggressorOrder = getAggressorOrder(initialOrderId)

  useEffect(() => {
    if (
      initialAggressorOrder &&
      initialAggressorOrder?.id !== '' &&
      (!aggressorOrderId || initialAggressorOrder.status !== 'creationPending')
    ) {
      setAggressorOrderId(initialAggressorOrder?.id)
    }
  }, [aggressorOrderId, initialAggressorOrder])

  return aggressorOrderId ? getOrder(aggressorOrderId) : initialAggressorOrder
}

export const useAggressorModalOrders = (windowInfo: AggressorWindowInfo) => {
  const dispatch = useAppDispatch()
  const getDOMOrderById = useAppSelector(getDepthOfMarketOrder)

  const domOrder = getDOMOrderById(
    windowInfo.initialOrderId,
    windowInfo.securityId
  )
  const firstAggressorOrder = useAggressorOrder(windowInfo.initialOrderId)

  const [firstInitialOrder, setFirstInitialOrder] = useState(domOrder)
  useEffect(() => {
    if (
      domOrder !== undefined ||
      firstAggressorOrder?.status === 'creationPending'
    ) {
      setFirstInitialOrder(domOrder)
    }
  }, [domOrder, firstAggressorOrder])

  useEffect(() => {
    const callId = `aggress-${windowInfo.initialOrderId}`
    dispatch(
      subscribeDepthForSecurity(
        windowInfo.securityId,
        QuoteReliability.All,
        callId
      )
    )
    return () => {
      dispatch(unsubscribeDepthForSecurity(windowInfo.securityId, callId))
    }
  }, [])

  return {
    initialOrder: firstInitialOrder
  }
}

export const useAggressorOrderForAggressorWindow = (transactionId: number) => {
  const initialAggressorOrder = useAppSelector(getOrderByTransactionId)(
    transactionId
  )
  const [currentAggressorOrder, setCurrentAggressorOrder] = useState(
    initialAggressorOrder
  )
  useEffect(() => {
    if (initialAggressorOrder !== undefined) {
      setCurrentAggressorOrder(initialAggressorOrder)
    }
  }, [initialAggressorOrder])
  return currentAggressorOrder
}

export const useSecurityModalInfo = (orderId: Order['id'], size: number) => {
  const securityModalInfo = useAppSelector(getSecurityModalInfo)(orderId)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchDataForSecurityModal(orderId, size))

    return () => {
      dispatch(unsubscribeFetchDataForSecurityModal(orderId))
    }
  }, [orderId, size])

  return securityModalInfo
}

export const useValueChangeAlert = <T>(
  checkVal: string | undefined,
  exceptedStrings: string[],
  value: T,
  buySellAmount: number
) => {
  const previousValue = usePrevious(value)
  const [showAlert, setShowAlert] = useState(false)

  useEffect(() => {
    if (
      checkVal === undefined ||
      exceptedStrings.indexOf(checkVal) < 0 ||
      (checkVal === 'creationPending' && !buySellAmount)
    ) {
      if (value !== previousValue && previousValue !== undefined) {
        setShowAlert(true)
      }
    }
  }, [value])

  return showAlert
}
